/**
 * @fileoverview Safe JSON parsing utility with error handling
 * @module utils/safeJsonParse
 * @version 1.0.0
 * 
 * @description
 * Provides production-safe JSON.parse wrapper that prevents crashes from malformed data.
 * Returns default value on parse failure and optionally logs errors in development.
 * 
 * @example
 * // Basic usage
 * const data = safeJsonParse(jsonString, {});
 * 
 * // With custom default
 * const items = safeJsonParse(localStorage.getItem('items'), []);
 * 
 * // With error callback
 * const config = safeJsonParse(response, null, (err) => {
 *   trackError('Config parse failed', err);
 * });
 */

import { logger } from './logger.js';

/**
 * Safely parse JSON string with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {*} defaultValue - Value to return if parsing fails (default: null)
 * @param {Function} [onError] - Optional error callback
 * @returns {*} Parsed object or defaultValue on failure
 */
export function safeJsonParse(jsonString, defaultValue = null, onError = null) {
  // Handle null/undefined/empty input
  if (jsonString === null || jsonString === undefined || jsonString === '') {
    return defaultValue;
  }

  // Handle non-string input
  if (typeof jsonString !== 'string') {
    logger.warn(`safeJsonParse: Input is not a string, received type: ${typeof jsonString}`);
    return defaultValue;
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    logger.error('safeJsonParse: Parse failed', error);
    
    // Call error callback if provided
    if (onError && typeof onError === 'function') {
      onError(error);
    }
    
    return defaultValue;
  }
}

/**
 * Safely parse JSON with validation
 * @param {string} jsonString - The JSON string to parse
 * @param {Function} validator - Function to validate parsed data
 * @param {*} defaultValue - Value to return if parsing or validation fails
 * @returns {*} Parsed and validated object or defaultValue
 * 
 * @example
 * const user = safeJsonParseValidated(
 *   userJson,
 *   (data) => data && typeof data.id === 'number',
 *   { id: 0, name: 'Guest' }
 * );
 */
export function safeJsonParseValidated(jsonString, validator, defaultValue = null) {
  const parsed = safeJsonParse(jsonString, defaultValue);
  
  if (parsed === defaultValue) {
    return defaultValue;
  }
  
  try {
    if (validator(parsed)) {
      return parsed;
    } else {
      logger.warn('safeJsonParseValidated: Validation failed');
      return defaultValue;
    }
  } catch (error) {
    logger.error('safeJsonParseValidated: Validator threw error', error);
    return defaultValue;
  }
}

/**
 * Parse JSON with type checking
 * @param {string} jsonString - The JSON string to parse
 * @param {'object'|'array'|'string'|'number'|'boolean'} expectedType - Expected type
 * @param {*} defaultValue - Value to return if parsing fails or type doesn't match
 * @returns {*} Parsed data if type matches, otherwise defaultValue
 * 
 * @example
 * const items = safeJsonParseTyped(data, 'array', []);
 * const config = safeJsonParseTyped(settings, 'object', {});
 */
export function safeJsonParseTyped(jsonString, expectedType, defaultValue = null) {
  const parsed = safeJsonParse(jsonString, defaultValue);
  
  if (parsed === defaultValue) {
    return defaultValue;
  }
  
  // Type checking
  const actualType = Array.isArray(parsed) ? 'array' : typeof parsed;
  
  if (actualType !== expectedType) {
    logger.warn(
      `safeJsonParseTyped: Type mismatch. Expected ${expectedType}, got ${actualType}`
    );
    return defaultValue;
  }
  
  return parsed;
}
